import { SortDirection } from '@cast/design-system';
import { useDebounce } from '@cast/utils';

import { RELIABILITY_REFETCH_INTERVAL } from 'features/cluster/_components/reliability/_constants/reliabilityRefetchInterval';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { usePaginatedWorkloadReliabilityMetricsQuery } from 'hooks/queries/cost/usePaginatedWorkloadReliabilityMetricsQuery';
import { useParams } from 'hooks/useParams';

import { UseServerData } from '@cast/shared-search-ui';

export const useReliabilityWorkloads: UseServerData = ({
  values: _values,
  sortingState,
  enabled,
}) => {
  const { clusterId } = useParams<{ clusterId: string }>();
  const { namespaces, workloadNames, workloadTypes } = useDebounce(_values);
  const {
    adjustedDateRange,
    timezone,
    capabilitiesError,
    isCapabilitiesLoading,
    refetchCapabilities,
    useListingPrices,
    includeIdleCosts,
    currentDateRangeFilter,
    hasReliabilityMetrics,
  } = useCostReportContext();

  const [from, to] = adjustedDateRange;
  const refetchInterval = currentDateRangeFilter
    ? RELIABILITY_REFETCH_INTERVAL[currentDateRangeFilter]
    : undefined;
  const sortField = sortingState?.columnId;
  const sortOrder = sortingState?.sortBy.direction as SortDirection;

  const res = usePaginatedWorkloadReliabilityMetricsQuery({
    clusterId,
    startTime: from.toISOString(),
    endTime: to.toISOString(),
    timezone,
    namespaces,
    workloadNames,
    workloadTypes,
    sortField,
    sortOrder,
    useListingPrices,
    includeIdleResourceCosts: includeIdleCosts,
    enabled: enabled && hasReliabilityMetrics,
    refetchInterval,
  });

  return {
    data: res.workloads,
    isLoading: res.isLoading || isCapabilitiesLoading,
    hasNextPage: !res.isLoading && res.hasNextPage,
    fetchNextPage: res.fetchNextPage,
    hasPreviousPage: !res.isLoading,
    hasFiltersApplied:
      !!namespaces?.length ||
      !!workloadNames?.length ||
      !!workloadTypes?.length,
    error: res.error || capabilitiesError,
    refetch: () => {
      if (hasReliabilityMetrics) {
        res.refetch();
      }
      refetchCapabilities();
    },
    metadata: res.count,
    isFetching: res.isFetching,
  };
};
